import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useSharedLists } from "../context/SharedListContext";
import { db } from "../firebase/config";
import {
    addDoc,
    collection,
    doc,
    arrayUnion,
    updateDoc,
    onSnapshot,
    getDocs,
} from "firebase/firestore";
import { updateListItem } from "../firebase/lists";

import ListHeader from "../components/ListHeader/ListHeader";
import ItemCard from "../components/ItemCard/ItemCard";
import Modal from "../components/Modal/Modal";
import AddItemForm from "../components/Forms/AddItemForm";
import InviteUserForm from "../components/Forms/InviteUserForm";
import AddListForm from "../components/Forms/AddListForm";
import DeleteItemForm from "../components/Forms/DeleteItemForm";
import DeleteListForm from "../components/Forms/DeleteListForm";
import Spinner from "../components/Spinner/Spinner";
import Interests from "../components/Interests/Interests";

export default function List({ lists = [], setLists }) {
    const { listId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { user, loading } = useAuth();
    const { joinListWithToken } = useSharedLists();

    const [currentList, setCurrentList] = useState(null);
    const [loadingList, setLoadingList] = useState(true);
    const [allUsers, setAllUsers] = useState([]);
    const params = new URLSearchParams(location.search);
    const [joinedBanner, setJoinedBanner] = useState(params.get("joined") === "true");

    const [assignedRecipient, setAssignedRecipient] = useState(null);

    const [isItemModalOpen, setItemModalOpen] = useState(false);
    const [editItemData, setEditItemData] = useState(null);
    const [isInviteModalOpen, setInviteModalOpen] = useState(false);
    const [editList, setEditList] = useState(null);
    const [isAddListOpen, setAddListOpen] = useState(false);
    const [deleteItem, setDeleteItem] = useState(null);
    const [deleteListModalOpen, setDeleteListModalOpen] = useState(false);

    useEffect(() => {
        if (!listId || !user) return;

        const listRef = doc(db, "lists", listId);
        const unsubscribe = onSnapshot(
            listRef,
            (snapshot) => {
                if (snapshot.exists()) {
                    const listData = { id: snapshot.id, ...snapshot.data() };
                    setCurrentList(listData);
                } else {
                    setCurrentList(null);
                }
                setLoadingList(false);
            },
            (err) => {
                console.error("Error listening to list:", err);
                setLoadingList(false);
            }
        );

        return () => unsubscribe();
    }, [listId, user]);

    useEffect(() => {
        if (!currentList || !user) return;
        const token = new URLSearchParams(location.search).get("token");

        if (token && currentList.inviteTokens?.[token]) {
            joinListWithToken(currentList.id, token)
                .then(() => setJoinedBanner(true))
                .catch((err) => console.error("Error joining list via token:", err));
        }
    }, [currentList, location.search, user, joinListWithToken]);

    useEffect(() => {
        const fetchUsers = async () => {
            const snapshot = await getDocs(collection(db, "users"));
            setAllUsers(snapshot.docs.map((doc) => ({ uid: doc.id, ...doc.data() })));
        };
        fetchUsers();
    }, []);

    const memberNames = (currentList?.members || []).reduce((acc, memberUid) => {
        const memberInfo =
            currentList?.memberInfo?.find((m) => m.uid === memberUid) ||
            allUsers.find((u) => u.uid === memberUid) ||
            {};
        const fullName =
            memberInfo.firstName && memberInfo.lastName
                ? `${memberInfo.firstName} ${memberInfo.lastName}`
                : memberInfo.displayName || memberInfo.name || memberInfo.email?.split("@")[0] || "Member";
        acc[memberUid] = fullName;
        return acc;
    }, {});

    useEffect(() => {
        if (!currentList || !user || !currentList.participants) {
            setAssignedRecipient(null);
            return;
        }

        let participant;

        if (user.uid) {
            participant = currentList.participants.find(p => p.userId === user.uid);
        }

        if (!participant) {
            participant = currentList.participants.find(
                p => !p.userId && p.name === (user.displayName || user.email?.split("@")[0])
            );
        }

        if (participant?.assignedTo) {
            setAssignedRecipient({ name: participant.assignedTo });
        } else {
            setAssignedRecipient(null);
        }
    }, [currentList, user]);

    if (loading || loadingList) return <Spinner />;
    if (!user) return <p className="p-6">You must be logged in to view this list.</p>;
    if (!currentList) return <p className="p-6">List not found</p>;

    const isMember = (currentList.members || []).includes(user.uid);
    const isAdmin = user?.uid === currentList.ownerId;

    const itemsByUser = (currentList.items || []).reduce((acc, item) => {
        const addedByUid = item.addedBy?.uid;
        const addedByName = item.addedBy?.name || memberNames[addedByUid] || "Member";
        if (!acc[addedByUid]) acc[addedByUid] = [];
        acc[addedByUid].push({ ...item, addedByName });
        return acc;
    }, {});

    const yourItems = itemsByUser[user.uid] || [];

    const handleAddOrUpdateItem = async (item) => {
        if (!isMember) return;
        try {
            const cleanedItem = {
                ...item,
                price: item.price
                    ? Number(item.price.toString().replace(/[^0-9.-]+/g, ""))
                    : null,
            };

            let newItem;
            let updatedItems;

            if (!item.id) {
                const { id, ...itemData } = cleanedItem;
                const memberInfo = currentList.memberInfo?.find((m) => m.uid === user.uid);
                const memberName = memberInfo
                    ? memberInfo.displayName
                    : user?.displayName || user?.email || "Member";

                itemData.addedBy = { uid: user.uid, name: memberName };

                const docRef = await addDoc(
                    collection(db, "lists", currentList.id, "items"),
                    itemData
                );

                newItem = { id: docRef.id, ...itemData };
                const listRef = doc(db, "lists", currentList.id);
                await updateDoc(listRef, { items: arrayUnion(newItem) });

                updatedItems = [...(currentList.items || []), newItem];
            } else {
                await updateListItem(currentList.id, item.id, cleanedItem);
                updatedItems = (currentList.items || []).map((i) =>
                    i.id === item.id ? { ...i, ...cleanedItem } : i
                );
                newItem = { ...item, ...cleanedItem };
                const listRef = doc(db, "lists", currentList.id);
                await updateDoc(listRef, { items: updatedItems });
            }

            setCurrentList((prev) => ({ ...prev, items: updatedItems }));
            setLists((prev) =>
                prev.map((l) =>
                    l.id === currentList.id ? { ...l, items: updatedItems } : l
                )
            );
        } catch (err) {
            console.error("Error adding/updating item:", err);
        }
    };

    const handleDeleteItem = async (itemId) => {
        try {
            const updatedItems = (currentList.items || []).filter((i) => i.id !== itemId);
            const listRef = doc(db, "lists", currentList.id);
            await updateDoc(listRef, { items: updatedItems });
            setDeleteItem(null);
        } catch (err) {
            console.error("Error deleting item:", err);
        }
    };

    return (
        <div className="max-w-3xl mx-auto mt-20 min-h-full px-6 md:px-0">
            <ListHeader
                currentList={currentList}
                joinedBanner={joinedBanner}
                assignedRecipient={assignedRecipient}
                isMember={isMember}
                isAdmin={isAdmin}
                onEditList={(list) => {
                    setEditList(list);
                    setAddListOpen(true);
                }}
                onDeleteList={() => setDeleteListModalOpen(true)}
                onInviteUser={() => setInviteModalOpen(true)}
                onAddItem={() => setItemModalOpen(true)}
                variant={currentList.isSecretSanta ? "secretSanta" : "default"}
            />

            {/* Main Content */}
            <div className={currentList.isSecretSanta ? "pt-28" : "pt-20"}
            >
                {isMember && (
                    <Interests
                        currentList={currentList}
                        user={user}
                        setCurrentList={setCurrentList}
                    />
                )}

                {(!currentList.items || currentList.items.length === 0) && isMember ? (
                    <p className="text-sm italic mt-2">
                        You haven't added any items yet! Get started by adding a new item.
                    </p>
                ) : (
                    <div className="flex flex-wrap -mx-2 mt-4">
                        {yourItems.map((item) => (
                            <div
                                key={item.id}
                                className="w-full sm:w-1/2 lg:w-1/3 px-2 mb-4"
                            >
                                <ItemCard
                                    item={{ ...item, currentUserId: user.uid }}
                                    onEdit={() => {
                                        setEditItemData(item);
                                        setItemModalOpen(true);
                                    }}
                                    publicView={false}
                                    onDelete={() => setDeleteItem(item)}
                                />
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Modals */}
            {isItemModalOpen && (
                <Modal
                    isOpen={isItemModalOpen}
                    size="large"
                    onClose={() => {
                        setItemModalOpen(false);
                        setEditItemData(null);
                    }}
                >
                    <AddItemForm
                        addItem={handleAddOrUpdateItem}
                        editItem={editItemData}
                        list={currentList}
                        onClose={() => {
                            setItemModalOpen(false);
                            setEditItemData(null);
                        }}
                    />
                </Modal>
            )}

            {deleteItem && (
                <Modal
                    isOpen={!!deleteItem}
                    size="small"
                    onClose={() => setDeleteItem(null)}
                >
                    <DeleteItemForm
                        item={deleteItem}
                        onDelete={handleDeleteItem}
                        onClose={() => setDeleteItem(null)}
                    />
                </Modal>
            )}

            {isInviteModalOpen && (
                <Modal
                    isOpen={isInviteModalOpen}
                    size="small"
                    onClose={() => setInviteModalOpen(false)}
                >
                    <InviteUserForm
                        list={currentList}
                        onClose={() => setInviteModalOpen(false)}
                    />
                </Modal>
            )}

            {isAddListOpen && (
                <Modal
                    isOpen={isAddListOpen}
                    size="small"
                    onClose={() => {
                        setAddListOpen(false);
                        setEditList(null);
                    }}
                >
                    <AddListForm
                        editList={editList}
                        onClose={() => {
                            setAddListOpen(false);
                            setEditList(null);
                        }}
                    />
                </Modal>
            )}

            {deleteListModalOpen && (
                <Modal
                    isOpen={deleteListModalOpen}
                    size="small"
                    onClose={() => setDeleteListModalOpen(false)}
                >
                    <DeleteListForm
                        list={currentList}
                        onDelete={() => {
                            setLists(lists.filter((l) => l.id !== currentList.id));
                            navigate("/");
                        }}
                        onClose={() => setDeleteListModalOpen(false)}
                    />
                </Modal>
            )}
        </div>
    );
}
